import { type ReactElement, useEffect, useRef, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { UserProvider, useUser } from "./context/UserContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastMessageProvider } from "./context/ToastMessageContext";
import { SessionTimeoutModal } from "./components/modals/SessionTimeoutModal";
import { SessionLogoutToast } from "./components/SessionLogoutToast";
import { SkipLinks } from "./components/SkipLinks";
import { ChatbotButton } from "./components/ChatbotButton";
import { ChatbotOverlay } from "./components/ChatbotOverlay";
import { ChatbotViewportPortal } from "./components/ChatbotViewportPortal";
import { AppRoutes } from "./AppRoutes";
import { BfcacheAuthRecovery } from "./components/security/BfcacheAuthRecovery";
import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import { userManager } from "./config/cognito";
import { TokenService } from "./services/token.service";
import Modal from "./components/modals/Modal";
import { adminService } from "./services/admin.service";
import { authService } from "./services/auth.service";
import {
  CookieConsentBanner,
  hasAcceptedAnalyticsCookies,
} from "./components/public/CookieConsentBanner";
import { GoogleAnalytics4Loader } from "./components/public/GoogleAnalytics4Loader";

const cognitoAuthConfig: AuthProviderProps = {
  userManager,
  onSigninCallback: (): void => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

function AppGlobalChatbot(): ReactElement {
  const { userProfile } = useUser();
  const location = useLocation();
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const chatbotButtonRef = useRef<HTMLButtonElement>(null);

  const isPublicRoute = (() => {
    const pathname = location.pathname;
    return (
      pathname === "/" ||
      pathname === "/welcome" ||
      pathname === "/auth/callback" ||
      pathname === "/faq" ||
      pathname === "/privacy-policy" ||
      pathname === "/terms-of-service" ||
      pathname === "/cookie-policy" ||
      pathname === "/accessibility-statement"
    );
  })();

  const usageMode: "authenticated" | "public" =
    !isPublicRoute || TokenService.isInternalTokenValid() || !!userProfile
      ? "authenticated"
      : "public";

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && chatbotOpen) {
        setChatbotOpen(false);
        chatbotButtonRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return (): void => window.removeEventListener("keydown", handleEscape);
  }, [chatbotOpen]);

  useEffect(() => {
    // Public landing page "Ask AI Assistant" CTA opens the global chatbot
    // without coupling the landing page to this component's state.
    const handleOpenRequest = (): void => {
      setChatbotOpen(true);
    };

    window.addEventListener("evoly:chatbot-open", handleOpenRequest);
    return (): void =>
      window.removeEventListener("evoly:chatbot-open", handleOpenRequest);
  }, []);

  return (
    <ChatbotViewportPortal>
      <ChatbotButton
        ref={chatbotButtonRef}
        onClick={() => {
          setChatbotOpen(!chatbotOpen);
        }}
        isOpen={chatbotOpen}
      />

      <ChatbotOverlay
        isOpen={chatbotOpen}
        onClose={() => {
          setChatbotOpen(false);
          chatbotButtonRef.current?.focus();
        }}
        usageMode={usageMode}
      />
    </ChatbotViewportPortal>
  );
}

function ScrollToTopOnRouteChange(): ReactElement | null {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  return null;
}

function TermsOfUseGate(): ReactElement | null {
  const location = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pathname = location.pathname;
  const isPublicRoute =
    pathname === "/" ||
    pathname === "/welcome" ||
    pathname === "/auth/callback" ||
    pathname === "/faq" ||
    pathname === "/privacy-policy" ||
    pathname === "/terms-of-service" ||
    pathname === "/cookie-policy" ||
    pathname === "/accessibility-statement";

  const tokenPayload = TokenService.getInternalTokenPayload();
  const shouldShowGate =
    TokenService.isInternalTokenValid() &&
    !isPublicRoute &&
    tokenPayload?.termsOfUseAccepted !== true;

  if (!shouldShowGate) {
    return null;
  }

  const role = tokenPayload?.role;
  const organizationId = tokenPayload?.organizationId;

  async function handleAccept(): Promise<void> {
    if (!accepted) {
      setError("You must accept the Terms of Use to continue.");
      return;
    }
    if (!role) {
      setError("Session is missing role information. Please sign in again.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await adminService.acceptTermsOfUse();
      const refreshed = await authService.signIn(role, organizationId);
      if (!refreshed) {
        setError("Failed to refresh session. Please sign in again.");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to accept Terms of Use.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      title="Terms of Use"
      titleId="terms-of-use-gate-title"
      contentClassName="modal-medium"
      footer={
        <div className="row gx-3 gy-2 justify-content-center align-items-center">
          <div className="col-12 col-sm-6 col-md-5">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                void authService.logout();
              }}
              disabled={isSubmitting}
            >
              Sign off
            </button>
          </div>
          <div className="col-12 col-sm-6 col-md-5">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => {
                void handleAccept();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Accepting..." : "Accept"}
            </button>
          </div>
        </div>
      }
    >
      <p className="mb-3">
        To continue, please review and accept the Terms of Use.
      </p>
      <p className="mb-3">
        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
          Open Terms and Conditions
        </a>
      </p>
      <div className="form-check">
        <input
          id="terms-of-use-checkbox"
          className="form-check-input"
          type="checkbox"
          checked={accepted}
          onChange={(event) => {
            setAccepted(event.target.checked);
            if (event.target.checked) {
              setError(null);
            }
          }}
          disabled={isSubmitting}
        />
        <label className="form-check-label" htmlFor="terms-of-use-checkbox">
          I accept the Terms of Use
        </label>
      </div>
      {error && <p className="text-danger mt-2 mb-0">{error}</p>}
    </Modal>
  );
}

function App() {
  const [analyticsAllowed, setAnalyticsAllowed] = useState(
    hasAcceptedAnalyticsCookies(),
  );

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const isLive = host === "evolyapp.com" || host === "www.evolyapp.com";
    const robotsContent = isLive ? "index,follow" : "noindex,nofollow";
    let robotsMeta = document.querySelector('meta[name="robots"]');

    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", robotsContent);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider {...cognitoAuthConfig}>
        <ToastMessageProvider>
          <UserProvider>
            <BrowserRouter>
              <GoogleAnalytics4Loader enabled={analyticsAllowed} />
              <ScrollToTopOnRouteChange />
              <SessionLogoutToast />
              <BfcacheAuthRecovery />
              <SkipLinks />
              <AppRoutes />
              <SessionTimeoutModal />
              <AppGlobalChatbot />
              <TermsOfUseGate />
              <CookieConsentBanner
                onChoice={(choice) => {
                  setAnalyticsAllowed(choice === "accepted");
                }}
              />
            </BrowserRouter>
          </UserProvider>
        </ToastMessageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
